import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  /** @type {MutationObserver|undefined} */
  #mo = undefined;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Ensure exactly one visible slide is aria-hidden="false" on first paint
    // Pick the first slide that is not [hidden]
    this.#normalizeInitialSlide();

    // React when schedule toggles [hidden] on slides
    this.#mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'hidden') {
          this.#normalizeInitialSlide();
          break;
        }
        if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
          this.#normalizeInitialSlide();
          break;
        }
      }
    });
  this.#mo?.observe(this, { subtree: true, attributes: true, attributeFilter: ['hidden'], childList: true });

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

  /** @type {HTMLElement[]} */
  const slides = this.slides ?? [];
    if (!slides.length) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Get only visible slides (exclude those hidden via [hidden])
   */
  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden')) ?? [];
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  /**
   * Ensure only the first non-[hidden] slide is aria-hidden=false.
   * If none are visible, do nothing.
   */
  #normalizeInitialSlide() {
    /** @type {HTMLElement[]} */
    const slides = this.slides ?? [];
    if (!slides.length) return;
    slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== 0)));
    // Reset internal current index to 0 for consistency
    this.#current = 0;
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#mo?.disconnect();
    this.#mo = undefined;
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
